import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Card } from "@/components/ui";
import { useToast } from "@/contexts";
import { getCompetitionById } from "@/features/competitions/api";
import { getTeam, leaveTeam } from "@/features/competitions/teams";
import type { CompetitionSummary, TeamMember, Team as TeamType } from "@/types";

export default function TeamDetail() {
  const { id, teamId } = useParams({ from: "/competition/$id/teams/$teamId" }) as {
    id: string;
    teamId: string;
  };
  const navigate = useNavigate();
  const toast = useToast();
  const [team, setTeam] = useState<(TeamType & { members: TeamMember[] }) | null>(null);
  const [competition, setCompetition] = useState<CompetitionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !teamId) return;
    (async () => {
      try {
        const [teamResult, compResult] = await Promise.all([
          getTeam(id, teamId),
          getCompetitionById(id),
        ]);
        setTeam(teamResult);
        if (compResult.data) setCompetition(compResult.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, teamId]);

  async function handleLeave() {
    if (!id || !teamId) return;
    try {
      await leaveTeam(id, teamId);
      toast.showAlert("Left team", "You left the team", "success");
      navigate({ to: `/competition/${id}/teams`, replace: true });
    } catch (err: unknown) {
      toast.showAlert(
        "Error",
        err instanceof Error ? err.message : "Failed to leave team",
        "error",
      );
    }
  }

  async function handleShare() {
    const inviteCode = competition?.inviteCode;
    const text = inviteCode
      ? `Join my team "${team?.name}" in FootSlapping! Use code: ${inviteCode}`
      : `Join my team "${team?.name}" in FootSlapping!`;
    if (navigator.share) {
      await navigator.share({ text }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(text);
      toast.showAlert("Copied", "Team info copied to clipboard", "success");
    }
  }

  if (loading) {
    return (
      <Screen title="Team" eyebrow="Teams">
        <div className="animate-pulse bg-card rounded-[20px] p-4 h-40 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)]" />
      </Screen>
    );
  }

  if (!team) {
    return (
      <Screen title="Not found" eyebrow="Teams">
        <Card>
          <p className="text-text-muted text-sm">Team not found.</p>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen
      eyebrow={competition?.name ?? ""}
      title={team.group ? `${team.name} (${team.group.name})` : team.name}
    >
      <Card>
        <div className="flex flex-row items-center justify-between">
          <span className="text-text-dim text-xs font-bold uppercase tracking-wide">
            {competition?.teamScoring === "average" ? "Average steps" : "Total steps"}
          </span>
          <span className="text-text text-2xl font-extrabold">
            {competition?.teamScoring === "average"
              ? (team.avgSteps ?? 0).toLocaleString()
              : team.totalSteps.toLocaleString()}
          </span>
        </div>
        {competition?.teamScoring === "average" && (
          <div className="flex flex-row items-center justify-between">
            <span className="text-text-dim text-xs font-bold uppercase tracking-wide">
              Total steps
            </span>
            <span className="text-text font-bold">{team.totalSteps.toLocaleString()}</span>
          </div>
        )}
        <div className="flex flex-row items-center justify-between">
          <span className="text-text-dim text-xs font-bold uppercase tracking-wide">Members</span>
          <span className="text-text font-bold">{team.memberCount}</span>
        </div>
      </Card>

      <div className="bg-card rounded-[20px] p-4 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)] flex flex-col gap-2">
        <span className="text-text-dim text-xs font-bold uppercase tracking-wide mb-1">
          Members
        </span>
        {(!team.members || team.members.length === 0) && (
          <p className="text-text-muted text-sm italic">No members yet.</p>
        )}
        {team.members?.map((member) => (
          <div key={member.userId} className="flex flex-row items-center gap-3 py-1.5">
            <div className="w-8 h-8 rounded-full bg-[rgba(255,45,126,0.1)] flex items-center justify-center shrink-0">
              <span className="text-aqua text-xs font-extrabold">
                {member.displayName?.charAt(0)?.toUpperCase() ?? "?"}
              </span>
            </div>
            <span className="flex-1 text-text text-sm font-bold">{member.displayName}</span>
            <span className="text-text-dim text-xs font-bold uppercase">
              {member.role === "captain" ? "Captain" : ""}
            </span>
            <span className="text-text-muted text-sm">{member.steps?.toLocaleString()}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-row gap-3">
        <button
          type="button"
          onClick={handleShare}
          className="flex-1 bg-aqua rounded-2xl py-3.5 px-5 items-center justify-center border border-text shadow-[1px_1px_0px_0px_#1c1b2e] cursor-pointer font-inherit"
        >
          <span className="text-white text-sm font-extrabold">Share invite</span>
        </button>
        <button
          type="button"
          onClick={handleLeave}
          className="flex-1 bg-white rounded-2xl py-3.5 px-5 items-center justify-center border border-text cursor-pointer font-inherit"
        >
          <span className="text-coral text-sm font-extrabold">Leave team</span>
        </button>
      </div>
    </Screen>
  );
}
